import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, ChevronLeft, ChevronRight, Loader2, Plus, SearchX } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCrmMeta } from "@/hooks/useCrmMeta";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChannelBadge, PageHeader, StageBadge } from "@/components/crm/Badges";
import { LeadFilters, EMPTY_FILTERS } from "@/components/crm/LeadFilters";
import { NewLeadDialog } from "@/components/crm/NewLeadDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { MassReassignDialog } from "@/components/crm/MassReassignDialog";

const COLS = ["Nombre", "Teléfono", "Canal", "Servicio", "Vendedor", "Estado", "Fecha ingreso", "Próxima acción"];
const PAGE_SIZE = 50;

export default function LeadsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { services, sellers, categories } = useCrmMeta();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [massOpen, setMassOpen] = useState(false);
  const isManager = user.role !== "vendedor";

  useEffect(() => {
    const t = setTimeout(() => {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== "all"));
      setLoading(true);
      api.get("/leads", { params: { ...params, page, page_size: PAGE_SIZE } })
        .then((r) => { setLeads(r.data.items); setTotal(r.data.total); })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [filters, reload, page]);
  useEffect(() => { setPage(1); setSelected(new Set()); setAllMatching(false); }, [filters]);
  const pageIds = leads.map((l) => l.id);
  const pageAll = pageIds.length > 0 && pageIds.every((i) => selected.has(i));
  const toggle = (id) => { setAllMatching(false); setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const togglePage = () => { setAllMatching(false); setSelected((s) => { const n = new Set(s); pageIds.forEach((i) => (pageAll ? n.delete(i) : n.add(i))); return n; }); };
  const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== "all"));
  const selection = allMatching ? { count: total, body: { filters: activeFilters } } : { count: selected.size, body: { lead_ids: [...selected] } };
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total ? (page - 1) * PAGE_SIZE + 1 : 0;

  return (
    <div data-testid="leads-page">
      <PageHeader title="Leads" subtitle={user.role === "vendedor" ? "Tus leads asignados" : "Todos los leads del centro"}>
        <Button onClick={() => setCreating(true)} className="rounded-xl bg-[#1E6FD9] hover:bg-[#0B2545]" data-testid="new-lead-button">
          <Plus className="mr-1.5 h-4 w-4" /> Nuevo lead
        </Button>
      </PageHeader>
      <LeadFilters filters={filters} setFilters={setFilters} services={services} sellers={sellers} categories={categories} showSeller={user.role !== "vendedor"} />
      {isManager && (selected.size > 0 || allMatching) && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-[#1E6FD9]/20 bg-[#1E6FD9]/[0.04] px-4 py-3 text-sm" data-testid="mass-selection-bar">
          <span data-testid="mass-selection-count"><b>{selection.count.toLocaleString("es-EC")}</b> leads seleccionados</span>
          {!allMatching && pageAll && total > pageIds.length && (
            <button className="font-semibold text-[#1E6FD9]" onClick={() => setAllMatching(true)} data-testid="select-all-matching">Seleccionar los {total.toLocaleString("es-EC")} que coinciden con el filtro</button>
          )}
          <button className="text-slate-500" onClick={() => { setSelected(new Set()); setAllMatching(false); }} data-testid="mass-selection-clear">Limpiar</button>
          <Button size="sm" className="ml-auto rounded-xl bg-[#1E6FD9]" onClick={() => setMassOpen(true)} data-testid="mass-reassign-button">Reasignar</Button>
        </div>
      )}
      <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3 text-xs text-slate-500">
          <span data-testid="leads-count">{total.toLocaleString("es-EC")} leads{total > 0 && <span className="text-slate-400"> · mostrando {from}–{from + leads.length - 1}</span>}</span>
          <span className="flex items-center gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin text-[#1E6FD9]" />}
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-lg" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                    aria-label="Página anterior" data-testid="leads-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
            <span data-testid="leads-page-indicator">Página {page} de {pages}</span>
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-lg" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
                    aria-label="Página siguiente" data-testid="leads-next-page"><ChevronRight className="h-4 w-4" /></Button>
          </span>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="leads-table">
            <TableHeader>
              <TableRow className="bg-[#F5F7FA]/60 hover:bg-[#F5F7FA]/60">
                {isManager && <TableHead className="w-10"><Checkbox checked={pageAll} onCheckedChange={togglePage} aria-label="Seleccionar página" data-testid="select-page-checkbox" /></TableHead>}
                {COLS.map((c) => <TableHead key={c} className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-slate-500">{c}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((l) => (
                <TableRow key={l.id} onClick={() => navigate(`/crm/leads/${l.id}`)} className="cursor-pointer transition-colors hover:bg-[#1E6FD9]/[0.03]"
                          data-testid={`lead-row-${l.id}`}>
                  {isManager && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={allMatching || selected.has(l.id)} onCheckedChange={() => toggle(l.id)} aria-label="Seleccionar" data-testid={`lead-select-${l.id}`} />
                    </TableCell>
                  )}
                  <TableCell className="whitespace-nowrap font-semibold text-[#0B2545]">
                    {l.full_name}
                    {l.has_appointment_request && <CalendarClock className="ml-1.5 inline h-3.5 w-3.5 text-amber-500" aria-label="Solicitud de cita" />}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-slate-600">{l.phone}</TableCell>
                  <TableCell><ChannelBadge channel={l.channel} /></TableCell>
                  <TableCell className="whitespace-nowrap text-slate-600">{l.service_name || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-slate-600" data-testid={`lead-row-seller-${l.id}`}>
                    {l.assigned_seller_name || <span className="font-medium text-rose-600">Sin asignar</span>}
                  </TableCell>
                  <TableCell><StageBadge stage={l.stage} /></TableCell>
                  <TableCell className="whitespace-nowrap text-slate-500">{fmtDate(l.created_at)}</TableCell>
                  <TableCell className="min-w-[180px] text-slate-600">
                    {l.next_action ? <span>{l.next_action}{l.next_action_date && <span className="block text-xs text-slate-400">{fmtDate(l.next_action_date)}</span>}</span> : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!loading && leads.length === 0 && (
          <div className="flex flex-col items-center py-16 text-slate-500" data-testid="leads-empty">
            <SearchX className="h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm font-medium">No se encontraron leads</p>
          </div>
        )}
      </div>
      <MassReassignDialog open={massOpen} onOpenChange={setMassOpen} sellers={sellers} selection={selection}
                          onDone={() => { setSelected(new Set()); setAllMatching(false); setReload((r) => r + 1); }} />
      <NewLeadDialog open={creating} onOpenChange={setCreating} services={services.filter((s) => s.active)} onDone={() => setReload((n) => n + 1)} />
    </div>
  );
}
